import { Frustum, Vector3 } from 'three';

import { Tiles3tzTile } from './tiles-3tz-tree';
import { TileCoordinates } from './types';

/** The camera, in the block's frame, see Tiles3tzModel. */
export type Tiles3tzView = {
  frustum: Frustum;
  /** A perspective camera's position. An orthographic camera has none: its scale is the same everywhere. */
  cameraPosition?: Vector3;
  /** Screen pixels a metre covers, 1 m from a perspective camera. */
  pixelsPerMetre: number;
  /** The screen-space error, in pixels, past which a tile is refined. */
  errorTarget: number;
  /** The finest level drawn, 0 being the root's; all levels when undefined. */
  maxLevel?: number;
};

export type Tiles3tzSelection = {
  /** The loaded tiles to draw. */
  drawn: Tiles3tzTile[];
  /**
   * Loaded tiles not drawn, but needed: the children replacing a drawn tile, and the tiles replaced
   * by their children, which their parents refine through only while they are loaded.
   */
  kept: Tiles3tzTile[];
  /** The tiles to load, coarsest, then those in view, then the most needed first. */
  wanted: Tiles3tzTile[];
  /** The subtrees to load, below tiles to refine. */
  subtrees: TileCoordinates[];
};

export type TileStates = {
  isLoaded(tile: Tiles3tzTile): boolean;
  /** Whether it failed so often it is no longer tried, so its parent doesn't wait for it. */
  hasFailedForGood(tile: Tiles3tzTile): boolean;
  /** The same for the subtree rooted at the tile. */
  hasSubtreeFailedForGood(root: TileCoordinates): boolean;
};

/** How many pixels the tile's geometric error covers on screen. */
export function screenSpaceError(tile: Tiles3tzTile, view: Tiles3tzView) {
  if (!view.cameraPosition) {
    return tile.geometricError * view.pixelsPerMetre;
  }
  const distance = Math.max(tile.box.distanceToPoint(view.cameraPosition), 1e-3);
  return (tile.geometricError * view.pixelsPerMetre) / distance;
}

/**
 * Picks the tiles to draw for the camera, and those to load. A tile is refined when its error
 * covers more than the target on screen, and with REPLACE refinement its children replace it only
 * once they have all loaded; until then it is drawn, so there are no holes. Its children out of view
 * load too, after those in view, and stay loaded: one coming into view as the camera moves is then
 * drawn at once, rather than the tile drawn again in place of the sharper children in view.
 */
export function select3tzTiles(
  root: Tiles3tzTile,
  view: Tiles3tzView,
  states: TileStates,
): Tiles3tzSelection {
  const drawn: Tiles3tzTile[] = [];
  const kept: Tiles3tzTile[] = [];
  const wanted: { tile: Tiles3tzTile; error: number; inView: boolean }[] = [];
  const subtrees: TileCoordinates[] = [];

  const isReady = (tile: Tiles3tzTile) =>
    !tile.content || states.isLoaded(tile) || states.hasFailedForGood(tile);
  /** Draws the tile once loaded, and loads it until then. */
  const show = (tile: Tiles3tzTile, error: number) => {
    if (!tile.content) {
      return;
    }
    if (states.isLoaded(tile)) {
      drawn.push(tile);
    } else {
      wanted.push({ tile, error, inView: true });
    }
  };

  const visit = (tile: Tiles3tzTile, error: number) => {
    // A subtree that failed for good holds no children to wait for
    const childSubtrees = tile.childSubtrees.filter(
      (childSubtree) => !states.hasSubtreeFailedForGood(childSubtree),
    );
    const hasChildren = tile.children.length > 0 || childSubtrees.length > 0;
    if (error <= view.errorTarget || !hasChildren || tile.level >= (view.maxLevel ?? Infinity)) {
      show(tile, error);
      return;
    }

    subtrees.push(...childSubtrees);
    const children = tile.children.map((child) => ({
      tile: child,
      error: screenSpaceError(child, view),
      inView: view.frustum.intersectsBox(child.box),
    }));
    if (childSubtrees.length === 0 && children.every((child) => isReady(child.tile))) {
      // Its parent refines only while it is loaded, so unloading it would draw the parent again
      if (tile.content && states.isLoaded(tile)) {
        kept.push(tile);
      }
      for (const child of children) {
        if (child.inView) {
          visit(child.tile, child.error);
        } else if (child.tile.content && states.isLoaded(child.tile)) {
          kept.push(child.tile);
        }
      }
      return;
    }

    show(tile, error);
    for (const child of children) {
      if (!child.tile.content) {
        continue;
      }
      if (states.isLoaded(child.tile)) {
        kept.push(child.tile);
      } else {
        wanted.push(child);
      }
    }
  };

  if (view.frustum.intersectsBox(root.box)) {
    visit(root, screenSpaceError(root, view));
  }

  wanted.sort(
    (a, b) =>
      a.tile.level - b.tile.level || Number(b.inView) - Number(a.inView) || b.error - a.error,
  );
  return { drawn, kept, wanted: wanted.map(({ tile }) => tile), subtrees };
}
